class ConditionalLogic {
  static settings = {
    hiddenClass: 'u-hidden',
  };

  constructor(element, options = {}) {
    this.element = element;

    this.settings = { ...ConditionalLogic.settings, ...options };

    this.conditionalLogic = this.parseConditionalLogic();
    this.dependentFields = this.getDependentFields();

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.init();
  }

  /**
   * Parse la logique conditionnelle depuis l'attribut data-conditional-logic
   */
  parseConditionalLogic() {
    try {
      const logicString = this.element.getAttribute('data-conditional-logic');
      return logicString ? JSON.parse(logicString) : null;
    } catch (error) {
      console.warn('Erreur lors du parsing de la logique conditionnelle:', error);
      return null;
    }
  }

  /**
   * Récupère tous les champs dont dépend ce champ conditionnel
   */
  getDependentFields() {
    if (!this.conditionalLogic) return [];

    const fieldIds = new Set();

    // Parcours récursif de la logique conditionnelle
    const extractFieldIds = (logic) => {
      if (Array.isArray(logic)) {
        logic.forEach(item => extractFieldIds(item));
      } else if (logic && typeof logic === 'object' && logic.field) {
        fieldIds.add(logic.field);
      }
    };

    extractFieldIds(this.conditionalLogic);

    return Array.from(fieldIds).map(fieldId => {
      const field = document.getElementById('acf-' + fieldId);
      if (!field) {
        console.warn(`Champ dépendant non trouvé: ${fieldId}`);
      }
      return field;
    }).filter(Boolean);
  }

  /**
   * Évalue une condition simple (un objet avec field, operator, value)
   */
  evaluateCondition(condition) {
    const { field: fieldId, operator, value } = condition;
    const field = document.getElementById('acf-' + fieldId);

    if (!field) return false;

    const fieldValue = this.getFieldValue(field);

    switch (operator) {
      case '==':
        return fieldValue == value;
      case '!=':
        return fieldValue != value;
      case '>':
        return parseFloat(fieldValue) > parseFloat(value);
      case '<':
        return parseFloat(fieldValue) < parseFloat(value);
      case '==contains':
        return String(fieldValue).includes(String(value));
      case '==empty':
        return !fieldValue || fieldValue === '';
      case '!=empty':
        return fieldValue && fieldValue !== '';
      default:
        console.warn(`Opérateur non supporté: ${operator}`);
        return false;
    }
  }

  /**
   * Récupère la valeur d'un champ selon son type
   */
  getFieldValue(field) {
    if (field.type === 'checkbox') {
      // Checkbox unique (true_false)
      return field.checked;
    } else if (field.type === 'radio') {
      // Radio
      const checkedRadio = document.querySelector(`input[name="${field.name}"]:checked`);
      return checkedRadio ? checkedRadio.value : '';
    } else if (field.tagName.toLowerCase() === 'select') {
      // Select
      return field.value;
    } else if (field.tagName.toLowerCase() === 'textarea' || field.tagName.toLowerCase() === 'input') {
      // Input classique, textarea
      return field.value;
    } else {
      // Cas par défaut
      console.warn('Type de champ non supporté:', field);
      return '';
    }
  }

  /**
   * Évalue un groupe de conditions (niveau AND)
   */
  evaluateConditionGroup(conditionGroup) {
    if (!Array.isArray(conditionGroup)) return false;

    return conditionGroup.every(condition => {
      if (Array.isArray(condition)) {
        // Sous-groupe (niveau OR)
        return this.evaluateConditionGroup(condition);
      } else {
        // Condition simple
        return this.evaluateCondition(condition);
      }
    });
  }

  /**
   * Évalue la logique conditionnelle complète
   */
  evaluateConditionalLogic() {
    if (!this.conditionalLogic) return true;

    // Premier niveau = OR
    return this.conditionalLogic.some(conditionGroup => {
      return this.evaluateConditionGroup(conditionGroup);
    });
  }

  /**
   * Met à jour la visibilité du champ
   */
  updateVisibility() {
    const shouldShow = this.evaluateConditionalLogic();

    // Trouver l'élément parent
    const parent = this.element.closest('.js-conditional-logic-parent');
    const targetElement = parent || this.element;

    if (shouldShow) {
      targetElement.classList.remove(this.settings.hiddenClass);
      this.enableField();
    } else {
      targetElement.classList.add(this.settings.hiddenClass);
      this.disableField();
    }
  }

  /**
   * Active le champ et ses inputs
   */
  enableField() {
    const inputs = this.element.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.disabled = false;
      input.removeAttribute('aria-disabled');
    });
  }

  /**
   * Désactive le champ et ses inputs
   */
  disableField() {
    const inputs = this.element.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
    });
  }

  /**
   * Gère les changements de valeur des champs dépendants
   */
  handleFieldChange() {
    this.updateVisibility();
  }

  /**
   * Initialise le composant
   */
  init() {
    if (!this.conditionalLogic) return;

    // Ajouter les listeners sur les champs dépendants
    this.dependentFields.forEach(field => {
      field.addEventListener('change', this.handleFieldChange);
      // field.addEventListener('input', this.handleFieldChange);
    });

    // Évaluation initiale
    this.updateVisibility();
  }

  /**
   * Nettoie les listeners
   */
  destroy() {
    this.dependentFields.forEach(field => {
      field.removeEventListener('change', this.handleFieldChange);
      // field.removeEventListener('input', this.handleFieldChange);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialisation des champs avec logique conditionnelle
  // (le champ conditionnel doit avoir la classe .js-form-field et l'attribut data-conditional-logic)
  // (le parent à masquer doit avoir la classe .js-conditional-logic-parent)
  const conditionalFields = document.querySelectorAll('.js-form-field[data-conditional-logic]');

  if (conditionalFields.length === 0) {
    return;
  }

  const instances = [];

  // Créer une instance pour chaque champ conditionnel
  conditionalFields.forEach(field => {
    try {
      const instance = new ConditionalLogic(field);
      instances.push(instance);
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du champ conditionnel:', error);
    }
  });
});
